import { gql } from 'graphql-request';
import { client } from '@/graph/client';
import { listUserPool } from './types/listUserPool';
import { listUsers } from './types/listUsers';
import { awsCognitoIdentityUserDetailResponse, getUser } from './types/getUser';
import { awsCognitoIdentityCreateUserResponse, createUser } from './types/createUser';
import { awsCognitoIdentityEnableStateUserResponse, enableUser } from './types/enableUser';
import { awsCognitoIdentityTemporaryPasswordUserResponse, tempPwUser } from './types/tempPwUser';
import { awsCognitoIdentityDeleteUserResponse, deleteUser } from "@/graph/aws/cognito/types/deleteUser";

export type CognitoListUserPool = {
	id: string,
	name: string,
};
export type CognitoListUser = {
	username: string,
	created: string,
	status: string,
	enabled: boolean,
};

export type CognitoGetUser = awsCognitoIdentityUserDetailResponse;

class Cognito {

	public async createUser(poolId: string, username: string, email: string, userkey: string): Promise<awsCognitoIdentityCreateUserResponse>
	{
		const out: awsCognitoIdentityCreateUserResponse = {
			success: false,
			message: "Unknown Error",
		};

		const query = gql`
			mutation awsCognitoCreateUser($poolId: String!, $username: String!, $email: String!, $userkey: String!) {
				awsCognitoCreateUser(
					poolId: $poolId
					username: $username
					email: $email
					userkey: $userkey
				)
				{
					success
					message
				}
			}
		`;

		const response = await client.request<createUser>(query, { poolId, username, email, userkey });

		console.log("createUser", response);

		if(response && response.awsCognitoCreateUser)
		{
			out.success = response.awsCognitoCreateUser.success;
			out.message = response.awsCognitoCreateUser.message;
		}

		return out;
	}

	public async enableUser(poolId: string, username: string, state: boolean): Promise<awsCognitoIdentityEnableStateUserResponse>
	{
		const out: awsCognitoIdentityEnableStateUserResponse = {
			success: false,
			message: "Unknown Error",
		};

		const query = gql`
			mutation awsCognitoEnableUser($poolId: String!, $username: String!, $enable: Boolean!) {
				awsCognitoEnableUser(
					poolId: $poolId
					username: $username
					enable: $enable
				)
				{
					success
					message
				}
			}
		`;

		const response = await client.request<enableUser>(query, { poolId, username, enable: state });

		console.log("createUser", response);

		if(response && response.awsCognitoEnableUser)
		{
			out.success = response.awsCognitoEnableUser.success;
			out.message = response.awsCognitoEnableUser.message;
		}

		return out;
	}

	public async tempPasswordForUser(poolId: string, username: string): Promise<awsCognitoIdentityTemporaryPasswordUserResponse>
	{
		const out: awsCognitoIdentityTemporaryPasswordUserResponse = {
			success: false,
			message: "Unknown Error",
		};

		const query = gql`
			mutation awsCognitoTemporaryPasswordUser($poolId: String!, $username: String!) {
				awsCognitoTemporaryPasswordUser(
					poolId: $poolId
					username: $username
				)
				{
					success
					message
				}
			}
		`;

		const response = await client.request<tempPwUser>(query, { poolId, username });

		console.log("createUser", response);

		if(response && response.awsCognitoTemporaryPasswordUser)
		{
			out.success = response.awsCognitoTemporaryPasswordUser.success;
			out.message = response.awsCognitoTemporaryPasswordUser.message;
		}

		return out;
	}

	public async listUserPool(maxResults?: number | undefined, nextToken?: string | undefined): Promise<CognitoListUserPool[]>
	{
		const out:CognitoListUserPool[] = [];

		const query = gql`
			{
				awsCognitoListUserPools
				{
					nextToken
					userPools {
						id
						name
					}
				}
			}
		`;

		const response = await client.request<listUserPool>(query);

		console.log("listUserPool", response);

		if(response.awsCognitoListUserPools.userPools)
		{
			for(const pool of response.awsCognitoListUserPools.userPools)
			{
				if(pool.id && pool.name)
				{
					out.push({
						id: pool.id,
						name: pool.name,
					});
				}
			}
		}

		return out;
	}

	public async listUsers(poolId: string, maxResults?: number | undefined, nextToken?: string | undefined): Promise<CognitoListUser[]>
	{
		const out:CognitoListUser[] = [];

		const query = gql`
			{
				awsCognitoListUsers(poolId: "` + poolId + `")
				{
					users {
						username
						created
						status
						enabled
					}
					nextToken
				}
			}
		`;

		const response = await client.request<listUsers>(query);

		console.log("listUsers", response);

		if(response.awsCognitoListUsers.users)
		{
			for(const user of response.awsCognitoListUsers.users)
			{
				if(user.username)
				{
					out.push({
						username: user.username,
						created: user.created ?? "1900-01-01T00:00:00+00:00",
						status: user.status ?? "unknown",
						enabled: user.enabled ?? false,
					});
				}
			}
		}

		return out;
	}

	public async getUser(poolId: string, username: string, maxResults?: number | undefined, nextToken?: string | undefined): Promise<CognitoGetUser | null>
	{
		const query = gql`
			{
				awsCognitoGetUser(poolId: "` + poolId + `", username: "` + username + `")
				{
					username
					created
					status
					enabled
					attributes {
						key
						value
					}
				}
			}
		`;

		const response = await client.request<getUser>(query);

		console.log("getUser", response);

		if(response.awsCognitoGetUser)
		{
			const user = response.awsCognitoGetUser;
			return user;
		}

		return null;
	}

	public async deleteUser(poolId: string | null, username: string): Promise<awsCognitoIdentityDeleteUserResponse> {
		const out: awsCognitoIdentityDeleteUserResponse = {
			success: false,
			message: "Unknown Error",
		};

		const query = gql`
			mutation awsCognitoDeleteUser($poolId: String!, $username: String!) {
				awsCognitoDeleteUser(
					poolId: $poolId
					username: $username
				)
				{
					success
					message
				}
			}
		`;

		const response = await client.request<deleteUser>(query, { poolId, username });

		console.log("deleteUser", response);

		if(response && response.awsCognitoDeleteUser)
		{
			out.success = response.awsCognitoDeleteUser.success;
			out.message = response.awsCognitoDeleteUser.message;
		}

		return out;
	}

	/**
	mutation {
		awsCognitoSetUserAttribute(poolId: "eu-central-1_rEVA3p9o4", username: "adammildner", 
			attributeKey: "custom:key", 
			attributeValue: "a969af35-c0c5-4ef1-ac58-d4ab42aa6e59"
		)
		{
			success
		}
	}
	 */

}

export default new Cognito();